/**
 * SelectUI - 基于 Bootstrap 的下拉框控制组件
 * 提供可动态添加/删除选项的下拉框组件，支持onChange事件
 */

import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';

export interface SelectOption {
  /** 选项值 */
  value: string;
  /** 显示文本 */
  text: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否选中 */
  selected?: boolean;
}

export interface SelectOptions extends ElementOptions {
  /** 标签文本 */
  label?: string;
  /** 是否显示标签 */
  showLabel?: boolean;
  /** 占位符文本 */
  placeholder?: string;
  /** 初始选项列表 */
  options?: SelectOption[];
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否多选 */
  multiple?: boolean;
  /** 下拉框样式类 */
  selectClass?: string;
  /** 选项容器样式类 */
  optionsContainerClass?: string;
  /** 添加按钮文本 */
  addButtonText?: string;
  /** 删除按钮文本 */
  removeButtonText?: string;
}

export class SelectUI {
  private ui: BootstrapUI;
  private container: HTMLElement;
  private selectElement: HTMLSelectElement;
  private options: SelectOptions;
  private onChangeCallbacks: ((value: string | string[], option: SelectOption | SelectOption[]) => void)[] = [];
  private optionsList: SelectOption[] = [];

  constructor(options: SelectOptions = {}) {
    this.ui = bootstrapUI;
    this.options = {
      label: '下拉选择',
      showLabel: true,
      placeholder: '请选择...',
      options: [],
      disabled: false,
      multiple: false,
      selectClass: 'form-select',
      optionsContainerClass: 'mt-2',
      ...options
    };
    
    this.optionsList = [...(this.options.options || [])];
    this.container = this.createElement();
    this.selectElement = this.container.querySelector('select') as HTMLSelectElement;
    
    this.setupEventHandlers();
    this.updateOptions();
  }

  /**
   * 创建下拉框组件元素
   * @returns 包含下拉框的容器元素
   */
  private createElement(): HTMLElement {
    // 添加vision-select-container样式类
    const container = this.ui.createElement({
      className: 'vision-select-container',
      ...this.options
    });

    // 创建标签（根据showLabel参数决定是否显示）
    if (this.options.showLabel && this.options.label) {
      const label = this.ui.createElement({
        tag: 'label',
        className: 'vision-select-label',
        textContent: this.options.label,
        attributes: {
          for: this.options.id || ''
        }
      });
      container.appendChild(label);
    }

    // 创建下拉框
    const select = this.ui.createElement({
      tag: 'select',
      className: this.options.selectClass || 'vision-select-control',
      attributes: {
        ...(this.options.id && { id: this.options.id }),
        ...(this.options.disabled && { disabled: 'true' }),
        ...(this.options.multiple && { multiple: 'true' })
      }
    }) as HTMLSelectElement;

    container.appendChild(select);

    return container;
  }

  /**
   * 设置事件处理器
   */
  private setupEventHandlers(): void {
    // 下拉框值变化事件
    this.ui.on('change', this.selectElement, (e) => {
      const target = e.target as HTMLSelectElement;
      console.log('target', target);
      if (this.options.multiple) {
        const selectedValues = Array.from(target.selectedOptions).map(option => option.value);
        const selectedOptions = this.optionsList.filter(option => selectedValues.includes(option.value));
        this.notifyChange(selectedValues, selectedOptions);
      } else {
        const selectedValue = target.value;
        const selectedOption = this.optionsList.find(option => option.value == selectedValue);
        this.notifyChange(selectedValue, selectedOption || { value: '', text: '' });
      }
    });
  }

  /**
   * 更新选项列表
   */
  private updateOptions(): void {
    // 清空现有选项
    this.selectElement.innerHTML = '';

    // 添加占位符选项（仅单选模式）
    if (!this.options.multiple && this.options.placeholder) {
      const placeholderOption = this.ui.createElement({
        tag: 'option',
        className: 'vision-select-control-options',
        textContent: this.options.placeholder,
        attributes: {
          value: '',
          disabled: 'true',
          selected: 'true'
        }
      }) as HTMLOptionElement;
      this.selectElement.appendChild(placeholderOption);
    }

    // 添加选项
    this.optionsList.forEach(option => {
      const optionElement = this.ui.createElement({
        tag: 'option',
        className: 'vision-select-control-options',
        textContent: option.text,
        attributes: {
          value: option.value,
          ...(option.disabled && { disabled: 'true' }),
          ...(option.selected && { selected: 'true' })
        }
      }) as HTMLOptionElement;
      this.selectElement.appendChild(optionElement);
    });
  }

  /**
   * 通知值变化
   * @param value 当前值
   * @param option 当前选项
   */
  private notifyChange(value: string | string[], option: SelectOption | SelectOption[]): void {
    console.log('notify------Change', value, option);
    this.onChangeCallbacks.forEach(callback => {
      try {
        console.log('call----back', value,option,callback);
        callback(value, option);
      } catch (error) {
        console.error('下拉框值变化回调执行错误:', error);
      }
    });
  }

  /**
   * 添加选项
   * @param option 要添加的选项
   */
  addOption(option: SelectOption): void {
    this.optionsList.push(option);
    this.updateOptions();
  }

  /**
   * 删除选项
   * @param index 要删除的选项索引
   */
  removeOption(index: number): void {
    if (index >= 0 && index < this.optionsList.length) {
      this.optionsList.splice(index, 1);
      this.updateOptions();
    }
  }

  /**
   * 根据值删除选项
   * @param value 要删除的选项值
   */
  removeOptionByValue(value: string): void {
    const index = this.optionsList.findIndex(option => option.value === value);
    if (index !== -1) {
      this.removeOption(index);
    }
  }

  /**
   * 清空所有选项
   */
  clearOptions(): void {
    this.optionsList = [];
    this.updateOptions();
  }

  /**
   * 设置选项列表
   * @param options 新的选项列表
   */
  setOptions(options: SelectOption[]): void {
    this.optionsList = [...options];
    this.updateOptions();
  }

  /**
   * 获取当前值
   * @returns 当前选中的值
   */
  getValue(): string | string[] {
    if (this.options.multiple) {
      return Array.from(this.selectElement.selectedOptions).map(option => option.value);
    } else {
      return this.selectElement.value;
    }
  }

  /**
   * 设置当前值
   * @param value 要设置的值
   */
  setValue(value: string | string[]): void {
    if (this.options.multiple && Array.isArray(value)) {
      // 多选模式
      Array.from(this.selectElement.options).forEach(option => {
        option.selected = value.includes(option.value);
      });
    } else if (!this.options.multiple && typeof value === 'string') {
      // 单选模式
      this.selectElement.value = value;
    }
  }

  /**
   * 获取当前选中的选项
   * @returns 当前选中的选项
   */
  getSelectedOption(): SelectOption | SelectOption[] {
    if (this.options.multiple) {
      const selectedValues = Array.from(this.selectElement.selectedOptions).map(option => option.value);
      return this.optionsList.filter(option => selectedValues.includes(option.value));
    } else {
      const selectedValue = this.selectElement.value;
      return this.optionsList.find(option => option.value === selectedValue) || { value: '', text: '' };
    }
  }

  /**
   * 是否禁用
   * @returns 是否禁用
   */
  isDisabled(): boolean {
    return this.selectElement.disabled;
  }

  /**
   * 设置禁用状态
   * @param disabled 是否禁用
   */
  setDisabled(disabled: boolean): void {
    this.selectElement.disabled = disabled;
    this.options.disabled = disabled;
  }

  /**
   * 设置组件激活状态（启用/禁用整个组件）
   */
  setActive(status: boolean): void {
    if (!status) {
      // 禁用整个组件
      this.container.classList.add('forbidden-event-dom');
       
      // 禁用下拉框
      this.setDisabled(true);
    } else {
      // 启用整个组件
      this.container.classList.remove('forbidden-event-dom');
      
      // 启用下拉框
      this.setDisabled(false);
    }
  }

  /**
   * 添加值变化监听器
   * @param callback 回调函数
   */
  onChange(callback: (value: string | string[], option: SelectOption | SelectOption[]) => void): void {
    this.onChangeCallbacks.push(callback);
  }

  /**
   * 移除值变化监听器
   * @param callback 要移除的回调函数
   */
  offChange(callback: (value: string | string[], option: SelectOption | SelectOption[]) => void): void {
    const index = this.onChangeCallbacks.indexOf(callback);
    if (index > -1) {
      this.onChangeCallbacks.splice(index, 1);
    }
  }

  /**
   * 获取下拉框元素
   * @returns 下拉框选择元素
   */
  getSelectElement(): HTMLSelectElement {
    return this.selectElement;
  }

  /**
   * 获取容器元素
   * @returns 容器元素
   */
  getContainer(): HTMLElement {
    return this.container;
  }

  /**
   * 获取选项列表
   * @returns 当前选项列表
   */
  getOptionsList(): SelectOption[] {
    return [...this.optionsList];
  }

  /**
   * 销毁下拉框组件
   */
  destroy(): void {
    this.onChangeCallbacks = [];
    this.ui.off('change', this.selectElement, () => {});
    
    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}


